import { Flex, IconButton } from '@wyz/ui';
import { useModalState } from '@wyz/utils';
import { useFormContext } from 'react-hook-form';
import { Trans } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import {
  QuoteDetailModal,
  QuoteEditItemPriceModal,
  QuoteProductCard,
  QuoteQuantitySelector,
} from '~/purchase/components';
import {
  IQuoteCartAction,
  IQuoteCartForm,
  IQuoteCartGroup,
  IQuoteCartItem,
  IQuoteItemInfo,
} from '~/purchase/types';

type Props = {
  item: IQuoteCartItem & { bfDiscount: number | null };
  vatPercentage: number;
  group: IQuoteCartGroup;
  actions: IQuoteCartAction;
  customer: IUserCustomer;
  showBfDiscount: boolean;
};

function CartItem({
  customer,
  item,
  vatPercentage,
  group,
  actions,
  showBfDiscount,
}: Props) {
  const [modal, modalHandlers] = useModalState<'DETAIL' | 'EDIT_PRICE'>();
  const methods = useFormContext<IQuoteCartForm>();

  const quantityName = `products.${item.id}.quantity` as const;
  const watchedQuantity = methods.watch(quantityName);
  const cartInfoItem = {
    tyre: item.tyre,
    stocks: item.stocks,
    proximityStocks: item.proximityStocks,
    pricing: {
      buyingPrice: item.buyingPrice,
      bfDiscount: item.bfDiscount,
      unitPriceTaxIncl: item.unitPriceTaxIncl,
      currency: item.currency,
    },
    manualDiscount: item.manualDiscount,
    availability: item.quoteAvailability,
    inStockQuantity: item.inStockQuantity,
    containsFallbackOffers: false,
    containsGspFallbackOffers: false,
  } satisfies IQuoteItemInfo;
  return (
    <QuoteProductCard
      customer={customer}
      item={cartInfoItem}
      buyingInfo={cartInfoItem.pricing}
      onShowDetails={() => modalHandlers.open('DETAIL')}
      onEditPrice={() => modalHandlers.open('EDIT_PRICE')}
      requestedQuantity={watchedQuantity}
      showBfDiscount={showBfDiscount}
      currency={item.currency}
      isFallback={false}
    >
      <div className='my-3'>
        <QuoteQuantitySelector
          name={quantityName}
          price={item.unitPriceTaxIncl}
          onChange={(quantity) => {
            actions.updateItemQuantity.process(item.id, { quantity });
          }}
          currency={item.currency}
        />
      </div>
      <Flex justifyContent='end'>
        <IconButton
          id={`remove-from-cart-${item.tyre.ean}`}
          icon='trash'
          iconSize='sm'
          variant='tertiary-grey'
          onClick={() => {
            actions.removeItem.process(item.id);
          }}
          isLoading={actions.removeItem.isLoading}
        >
          <Trans i18nKey='app.purchase.quote_search_page.product.delete' />
        </IconButton>
      </Flex>
      {modal === 'DETAIL' && (
        <QuoteDetailModal
          item={cartInfoItem}
          customer={customer}
          onClose={() => modalHandlers.close()}
          currency={item.currency}
        />
      )}
      {modal === 'EDIT_PRICE' && (
        <QuoteEditItemPriceModal
          group={group}
          vatPercentage={vatPercentage}
          onClose={() => modalHandlers.close()}
          actions={actions}
        />
      )}
    </QuoteProductCard>
  );
}

export default CartItem;
